/* The archive card. Four across on a desktop, one on a phone, borders
 * collapsing into a single printed sheet.
 *
 * STRUCTURE, and it is deliberate. The card is a <div>; the TITLE holds the one
 * real <a>, which is stretched over the whole card by a pseudo-element. The
 * genre chips and the play button then sit ABOVE that overlay and stay ordinary
 * links and buttons.
 *
 * Wrapping the entire card in an <a> is the obvious version and it is wrong
 * twice over: the genre chips are links, and a link inside a link is invalid
 * HTML that browsers silently repair in different ways — so the chip either
 * navigates to the episode or does nothing, depending on whose browser it is.
 * A <div onClick> is the other wrong version: no href to copy, no middle-click,
 * no Enter key, nothing for a screen reader to announce.
 *
 * The stretched link keeps the whole card clickable, keeps one honest href, and
 * leaves the chips working.
 *
 * The lift on hover is the card physically coming off the page — translate
 * (-3,-3) with the shadow GROWING to 9px, then a slam to (3,3) with the shadow
 * collapsing to nothing on press. 100ms linear. That is the whole motion
 * vocabulary of this site and there is nothing else in it.
 */

import { Link } from 'react-router-dom';

import { formatDuration } from '../format';
import type { TzMode } from '../format';
import type { EpisodeCard } from '../types';
import { Artwork } from './Artwork';
import { Chip, ChipList } from './Chip';
import { hostNames, shortDate } from './shared';

export interface EpisodeCardLinkProps {
  episode: EpisodeCard;
  tz: TzMode;
  /** Overrides the route. Defaults to the episode's own page. */
  to?: string;
  /**
   * Given, the card grows a play button. Without it the card is a link and
   * nothing else, which is right in a list nobody can play from.
   */
  onPlay?: (episode: EpisodeCard) => void;
  /** This episode is the one currently loaded in the player. */
  isPlaying?: boolean;
  /** Routes a genre chip. Without it the chips are labels, not links. */
  genreHref?: (slug: string) => string;
  className?: string;
}

export function EpisodeCardLink({
  episode,
  tz,
  to,
  onPlay,
  isPlaying = false,
  genreHref,
  className,
}: EpisodeCardLinkProps) {
  const href = to ?? `/episodes/${encodeURIComponent(episode.catalogue_no)}`;
  const who = hostNames(episode.hosts);

  const meta = [
    episode.catalogue_no,
    shortDate(episode.broadcast_at, tz),
    formatDuration(episode.duration_seconds),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={className ? `cardwrap ${className}` : 'cardwrap'}>
      <div className="card">
        <Artwork
          color={episode.artwork_color}
          imageUrl={episode.artwork_url}
          catalogueNo={episode.catalogue_no}
          label={episode.is_staff_pick ? 'Staff pick' : episode.catalogue_no}
          size="card"
        />
        <div className="card__body">
          <h3>
            <Link className="card__link" to={href}>
              {episode.title}
            </Link>
          </h3>
          {who ? <div className="mono dim card__host">{who}</div> : null}
          <div className="mono card__meta">{meta}</div>

          {episode.is_staff_pick ? (
            <div className="card__pick">
              <span className="chip chip--pick">Staff pick</span>
              {episode.staff_pick_note ? (
                <span className="mono dim card__picknote">{episode.staff_pick_note}</span>
              ) : null}
            </div>
          ) : null}

          {episode.genres.length > 0 ? (
            <ChipList label="Genres">
              {episode.genres.map((genre) => (
                <Chip
                  key={genre.slug}
                  color={genre.color_token}
                  to={genreHref ? genreHref(genre.slug) : undefined}
                >
                  {genre.name}
                </Chip>
              ))}
            </ChipList>
          ) : null}

          {episode.has_tracklist ? (
            <span className="mono dim card__tracklist">Has tracklist</span>
          ) : null}
        </div>
      </div>

      {onPlay ? (
        <button
          type="button"
          className="pbtn card__play"
          aria-label={`${isPlaying ? 'Pause' : 'Play'} ${episode.title}, ${episode.catalogue_no}`}
          aria-pressed={isPlaying}
          onClick={() => onPlay(episode)}
        >
          <span className={isPlaying ? 'pausebars' : 'tri'} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
